import { ethers } from 'ethers';
import React, { useEffect, useState } from 'react';
import { SkeletonTheme } from 'react-loading-skeleton';
import { Link } from 'react-router-dom';
import { useAccount } from 'wagmi';
import { CPGABI } from '../../ABI/ABI';
import { getStoredBackendToken, listMyDirectPayments, listMyPaymentRequests } from '../../api/backendClient';
import BackendWalletSignInPanel from '../../components/auth/BackendWalletSignInPanel';
import ConnectWalletButton from '../../components/connectWallet/ConnectWalletButton';
import SwitchNetworkButton from '../../components/connectWallet/SwitchNetworkButton';
import { useBackendWalletAuth } from '../../hooks/useBackendWalletAuth';
import Loader from '../../components/loader/Loader';
import { homeFilterData } from '../../config/data';
import { chainName, CPGAddress, initialTokenList, RECEIVE, SEND, targetChainId } from '../../constant/constant';
import { TRANSACTION_ERROR_MSG, USER_REJECT_TRANSACTION_MSG } from '../../constant/toasterMessage';
import ClaimPayment from '../../Modal/ClaimPayment';
import DisputeModal from '../../Modal/DisputeModal';
import { fireErrorToast, formatNumber, shortenWalletAddress } from '../../utils/commonFunction';
import AmountDisplay from '../../components/amount/AmountDisplay';
import { readContractData } from '../../utils/contractInstance';
import { sortDirectPaymentsNewestFirst } from '../../utils/directPaymentSort';
import { aggregateInEscrowMetrics } from '../../utils/lockedPaymentHistory';
import {
    calculateClaimableAmountWei,
    computeCustomClaimableWei,
    isLockedPaymentDisputed,
    isLockedPaymentFinished,
} from '../../utils/lockedPaymentClaimable';
import {
    fetchLockedPaymentSchedule,
    fetchLockedPaymentsByIds,
} from '../../utils/cpgDiamondReader';
import {
    buildHistoryFromDbRecords,
    mapPaymentRequestRecordForChart,
} from '../../utils/dashboardHistoryFromDb';
import { blockExplorerUrl, publicClient } from '../../utils/wagmiConfig';
import { useTheme } from '../../context/ThemeContext';
import DashboardAlerts from '../../components/notifications/DashboardAlerts';
import TransactionHistoryList from './components/TransactionHistoryList';
import TransactionVolumeChart from './components/TransactionVolumeChart';
import {
    buildDashboardChartDataset,
    buildEmptyChartBuckets,
} from '../../utils/dashboardChartData';
import {
    DEFAULT_DAILY_TIMELINE_DAYS,
    dailyPresetLabel,
} from '../../utils/chartTimeline';

const HISTORY_PAGE_SIZE = 10;

const findTokenInfo = (tokenAddress) => {
    const needle = String(tokenAddress || '').trim().toLowerCase();
    if (!needle) return null;
    return (
        initialTokenList.find(
            (token) => String(token.address || '').trim().toLowerCase() === needle,
        ) || null
    );
};

const Home = () => {
    const [filterName, setFilterName] = useState(SEND)
    const [currentItem, setCurrentItem] = useState({})
    const [disputeModal, setDisputeModal] = useState(false);
    const [collectPaymentData, setCollectPaymentData] = useState([])
    const [sendData, setSendData] = useState([])
    const [receiveData, setReceiveData] = useState([])
    const [sendDataLoading, setSendDataLoading] = useState(false)
    const [receiveDataLoading, setReceiveDataLoading] = useState(false)
    const [paymentId, setPaymentId] = useState(null)
    const [claimModal, setClaimModal] = useState(null)
    const [totalSendAmount, setTotalSendAmount] = useState(0)
    const [totalReceiveAmount, setTotalReceiveAmount] = useState(0)
    const [totalClaimableAmount, setTotalClaimableAmount] = useState(0)
    const [totalInEscrowAmount, setTotalInEscrowAmount] = useState(0)
    const [inEscrowPaymentCount, setInEscrowPaymentCount] = useState(0)
    const [inEscrowDisputedCount, setInEscrowDisputedCount] = useState(0)
    const [txHashMap, setTxHashMap] = useState({ init: {}, claim: {} })
    const [chartDataset, setChartDataset] = useState(() => buildEmptyChartBuckets())
    const [chartLoading, setChartLoading] = useState(false)
    const [chartTimelineDays, setChartTimelineDays] = useState(DEFAULT_DAILY_TIMELINE_DAYS)
    const [paymentRecords, setPaymentRecords] = useState([])
    const [outgoingPaymentRequests, setOutgoingPaymentRequests] = useState([])
    const [incomingPaymentRequests, setIncomingPaymentRequests] = useState([])
    const [paymentEventTimes, setPaymentEventTimes] = useState({})
    const [requestAcceptedEventTimes, setRequestAcceptedEventTimes] = useState({})
    const [historyPage, setHistoryPage] = useState(1)

    const { isConnected, address: walletAddress, chainId } = useAccount()
    const {
        status: authStatus,
        error: authError,
        signIn,
        isReady: isBackendReady,
    } = useBackendWalletAuth({
        walletAddress,
        enabled: isConnected && chainId === targetChainId,
    });
    const { resolvedTheme } = useTheme();
    const skeletonBase = resolvedTheme === 'light' ? '#e4e4e7' : '#18181b';
    const skeletonHighlight = resolvedTheme === 'light' ? '#f4f4f5' : '#3f3f46';

    // const chainId = useChainId();

    const onWriteSuccess = async (data) => {
        if (data) {
            // console.log("Success", data);
            if (data.transactionHash) {
                // setShowCongratulationModal(true)
            }
        }
    };



    const onWriteError = (error) => {
        if (error) {
            if (!error?.details?.includes('MetaMask Tx Signature: User denied transaction signature')) {
                fireErrorToast(TRANSACTION_ERROR_MSG)
            }
            if (error?.details?.includes('MetaMask Tx Signature: User denied transaction signature')) {
                fireErrorToast(USER_REJECT_TRANSACTION_MSG)
            }
        }

        // }
    }

    const loadHistoryRoleFromDb = async (role) => {
        const token = getStoredBackendToken(walletAddress);
        if (!token) {
            return { rows: [], totalDirectAmount: 0, directRecords: [], requestRecords: [] };
        }

        const [directRecords, requestRecords] = await Promise.all([
            listMyDirectPayments(token, { role }),
            listMyPaymentRequests(token, { role }),
        ]);

        const directs = Array.isArray(directRecords) ? directRecords : [];
        const requests = Array.isArray(requestRecords) ? requestRecords : [];
        const { rows, totalDirectAmount } = buildHistoryFromDbRecords({
            directRecords: directs,
            requestRecords: requests,
            role,
            walletAddress,
            tokenList: initialTokenList,
            chainId,
        });

        return {
            rows: sortDirectPaymentsNewestFirst(
                rows,
                paymentEventTimes,
                requestAcceptedEventTimes,
            ),
            totalDirectAmount,
            directRecords: directs,
            requestRecords: requests,
        };
    };

    const getReceiveDirectPaymentData = async () => {
        if (!walletAddress) return;
        try {
            setReceiveDataLoading(true);
            const { rows, totalDirectAmount, directRecords } =
                await loadHistoryRoleFromDb('received');
            setPaymentRecords((prev) => {
                const byKey = new Map(
                    (Array.isArray(prev) ? prev : []).map((r) => [
                        `${r.kind}:${r.paymentId}:${r.txHash}`,
                        r,
                    ]),
                );
                for (const r of directRecords) {
                    byKey.set(`${r.kind}:${r.paymentId}:${r.txHash}`, r);
                }
                return [...byKey.values()];
            });
            setTotalReceiveAmount(totalDirectAmount);
            setReceiveData(rows);
        } catch (error) {
            console.warn(
                'Failed to load receive history from DB:',
                error?.response?.data?.message || error?.message,
            );
        } finally {
            setReceiveDataLoading(false);
        }
    };

    const getPendingLockedClaims = async () => {
        if (!walletAddress) {
            setCollectPaymentData([]);
            setTotalClaimableAmount(0);
            return;
        }
        try {
            const token = getStoredBackendToken(walletAddress);
            let candidateIds = [];

            if (token) {
                const tracked = await listMyDirectPayments(token, {
                    role: 'received',
                    kind: 'tracked',
                });
                const wallet = String(walletAddress).toLowerCase();
                candidateIds = (Array.isArray(tracked) ? tracked : [])
                    .filter((row) => {
                        const status = String(row.status || '').toLowerCase();
                        if (status === 'claimed' || status === 'refunded') {
                            return false;
                        }
                        return (
                            String(row.receiver || '').toLowerCase() === wallet
                        );
                    })
                    .map((row) => Number(row.paymentId))
                    .filter((id) => Number.isFinite(id) && id > 0);
            }

            if (!candidateIds.length) {
                setCollectPaymentData([]);
                setTotalClaimableAmount(0);
                return;
            }

            const lockedPaymentsList = await fetchLockedPaymentsByIds(
                readContractData,
                CPGAddress,
                CPGABI,
                candidateIds,
            );

            const candidates = lockedPaymentsList.filter(
                (paymentData) =>
                    !isLockedPaymentFinished(paymentData) &&
                    !isLockedPaymentDisputed(paymentData),
            );

            const availability = await Promise.all(
                candidates.map(async (paymentData) => {
                    const paymentId = Number(paymentData.paymentId);
                    try {
                        const isMilestoneAvailable = await readContractData(
                            CPGAddress,
                            CPGABI,
                            'isNewMilestoneAvailable',
                            [paymentId],
                        );
                        return {
                            paymentData,
                            paymentId,
                            isMilestoneAvailable: Boolean(isMilestoneAvailable),
                        };
                    } catch (_error) {
                        return {
                            paymentData,
                            paymentId,
                            isMilestoneAvailable: false,
                        };
                    }
                }),
            );

            const pendingRows = [];
            let totalClaimable = 0;

            for (const {
                paymentData,
                paymentId,
                isMilestoneAvailable,
            } of availability) {
                if (!isMilestoneAvailable) continue;

                const tokenAddress =
                    paymentData.tokenAddress ?? paymentData.token;
                const tokenInfo =
                    findTokenInfo(tokenAddress) ||
                    {
                        name: 'USDC',
                        decimals: 6,
                        logoURI: '',
                    };

                const { name, decimals, logoURI } = tokenInfo;
                let claimableWei = '0';

                if (paymentData.customSchedule) {
                    const schedule = await fetchLockedPaymentSchedule(
                        readContractData,
                        CPGAddress,
                        CPGABI,
                        paymentId,
                    );
                    const { claimAmountWei } = computeCustomClaimableWei(
                        schedule,
                        paymentData.claimedMilestones,
                    );
                    claimableWei = claimAmountWei;
                } else {
                    claimableWei = calculateClaimableAmountWei(
                        paymentData.startTime,
                        paymentData.milestoneInterval,
                        paymentData.claimedMilestones,
                        paymentData.totalAmount,
                        paymentData.milestones,
                    );
                }

                const claimableFloat = parseFloat(
                    ethers.formatUnits(claimableWei, decimals),
                );
                if (!Number.isFinite(claimableFloat) || claimableFloat <= 0) {
                    continue;
                }

                const totalAmountFloat = parseFloat(
                    ethers.formatUnits(paymentData.totalAmount, decimals),
                );

                pendingRows.push({
                    ...paymentData,
                    id: paymentId,
                    paymentId,
                    chainIndex: paymentData.chainIndex,
                    sender: paymentData.sender,
                    amount: formatNumber(claimableFloat),
                    parsedNumber: claimableFloat,
                    totalAmountFloat,
                    tokenName: name,
                    tokenImage: logoURI,
                    tokenDecimal: decimals,
                    tokenAddress,
                    type: 'Milestone',
                    isClaimed: false,
                    isMilestoneAvailable: true,
                });
                totalClaimable += claimableFloat;
            }

            pendingRows.sort((a, b) => b.id - a.id);
            setCollectPaymentData(pendingRows);
            setTotalClaimableAmount(totalClaimable);
        } catch (error) {
            console.warn('Failed to load pending locked claims:', error?.message);
            setCollectPaymentData([]);
            setTotalClaimableAmount(0);
        }
    };

    const getSendDirectPaymentData = async () => {
        if (!walletAddress) return;
        try {
            setSendDataLoading(true);
            const { rows, totalDirectAmount, directRecords } =
                await loadHistoryRoleFromDb('sent');
            setPaymentRecords((prev) => {
                const byKey = new Map(
                    (Array.isArray(prev) ? prev : []).map((r) => [
                        `${r.kind}:${r.paymentId}:${r.txHash}`,
                        r,
                    ]),
                );
                for (const r of directRecords) {
                    byKey.set(`${r.kind}:${r.paymentId}:${r.txHash}`, r);
                }
                return [...byKey.values()];
            });
            setTotalSendAmount(totalDirectAmount);
            setSendData(rows);
        } catch (error) {
            console.warn(
                'Failed to load send history from DB:',
                error?.response?.data?.message || error?.message,
            );
        } finally {
            setSendDataLoading(false);
        }
    };

    const getPaymentRequestsForChart = async () => {
        if (!walletAddress) return;
        try {
            const token = getStoredBackendToken(walletAddress);
            if (!token) return;

            const [sentResults, receivedResults] = await Promise.all([
                listMyPaymentRequests(token, { role: 'sent' }),
                listMyPaymentRequests(token, { role: 'received' }),
            ]);

            setOutgoingPaymentRequests(
                (Array.isArray(sentResults) ? sentResults : [])
                    .map((row) =>
                        mapPaymentRequestRecordForChart(row, initialTokenList),
                    )
                    .filter(Boolean),
            );
            setIncomingPaymentRequests(
                (Array.isArray(receivedResults) ? receivedResults : [])
                    .map((row) =>
                        mapPaymentRequestRecordForChart(row, initialTokenList),
                    )
                    .filter(Boolean),
            );

            const times = {};
            for (const row of [
                ...(Array.isArray(sentResults) ? sentResults : []),
                ...(Array.isArray(receivedResults) ? receivedResults : []),
            ]) {
                if (String(row.status || '').toLowerCase() !== 'accepted') {
                    continue;
                }
                const requestId = Number(row.requestId);
                if (!Number.isFinite(requestId)) continue;
                const ms = new Date(row.updatedAt || row.createdAt).getTime();
                if (Number.isFinite(ms) && ms > 0) {
                    times[requestId] = ms;
                }
            }
            if (Object.keys(times).length) {
                setRequestAcceptedEventTimes((prev) => ({ ...times, ...prev }));
            }
        } catch (error) {
            console.warn(
                'Failed to fetch payment requests for dashboard chart:',
                error?.response?.data?.message || error?.message,
            );
        }
    };

    // Soft fill for hashes/timestamps still missing after DB load (short lookback only).
    const getDirectPaymentTxHashes = async () => {
        if (!walletAddress) return;
        try {
            const wallet = walletAddress.toLowerCase();
            const latest = await publicClient.getBlockNumber();
            const lookbackRaw = Number(
                process.env.REACT_APP_CPG_LOG_LOOKBACK_BLOCKS || 4_000,
            );
            const lookback = BigInt(
                Number.isFinite(lookbackRaw) && lookbackRaw > 0
                    ? Math.floor(lookbackRaw)
                    : 4_000,
            );
            const fromBlock = latest > lookback ? latest - lookback : 0n;
            const [
                directInitNewLogs,
                claimLogs,
                lockedInitCreatedLogs,
                lockedInitCustomLogs,
                lockedClaimLogs,
                requestResolvedLogs,
            ] = await Promise.all([
                publicClient.getContractEvents({
                    address: CPGAddress,
                    abi: CPGABI,
                    eventName: 'DirectPaymentCreated',
                    fromBlock,
                    toBlock: 'latest',
                }).catch(() => []),
                publicClient.getContractEvents({
                    address: CPGAddress,
                    abi: CPGABI,
                    eventName: 'DirectPaymentClaimed',
                    fromBlock,
                    toBlock: 'latest',
                }).catch(() => []),
                publicClient.getContractEvents({
                    address: CPGAddress,
                    abi: CPGABI,
                    eventName: 'LockedPaymentCreated',
                    fromBlock,
                    toBlock: 'latest',
                }).catch(() => []),
                publicClient.getContractEvents({
                    address: CPGAddress,
                    abi: CPGABI,
                    eventName: 'CustomLockedPaymentCreated',
                    fromBlock,
                    toBlock: 'latest',
                }).catch(() => []),
                publicClient.getContractEvents({
                    address: CPGAddress,
                    abi: CPGABI,
                    eventName: 'LockedPaymentClaimed',
                    fromBlock,
                    toBlock: 'latest',
                }).catch(() => []),
                publicClient.getContractEvents({
                    address: CPGAddress,
                    abi: CPGABI,
                    eventName: 'PaymentRequestResolved',
                    fromBlock,
                    toBlock: 'latest',
                }).catch(() => []),
            ]);

            const lockedInitLogs = [
                ...lockedInitCreatedLogs,
                ...lockedInitCustomLogs,
            ];
            const initLogs = [...directInitNewLogs];
            const requestAcceptedLogs = [...requestResolvedLogs];

            const relevantBlockNumbers = new Set();
            const trackWalletLog = (log) => {
                const sender = String(log.args?.sender || '').toLowerCase();
                const receiver = String(log.args?.receiver || '').toLowerCase();
                if (sender === wallet || receiver === wallet) {
                    relevantBlockNumbers.add(log.blockNumber);
                }
            };
            initLogs.forEach(trackWalletLog);
            lockedInitLogs.forEach(trackWalletLog);
            requestAcceptedLogs.forEach((log) => {
                const requester = String(log.args?.requester || '').toLowerCase();
                const payer = String(
                    log.args?.payer || log.args?.receiver || '',
                ).toLowerCase();
                if (requester === wallet || payer === wallet) {
                    relevantBlockNumbers.add(log.blockNumber);
                }
            });

            const blockTimeByNumber = {};
            const blockNumbers = [...relevantBlockNumbers];
            const BLOCK_BATCH = 12;
            for (let i = 0; i < blockNumbers.length; i += BLOCK_BATCH) {
                const slice = blockNumbers.slice(i, i + BLOCK_BATCH);
                await Promise.all(
                    slice.map(async (blockNumber) => {
                        try {
                            const block = await publicClient.getBlock({
                                blockNumber,
                            });
                            blockTimeByNumber[String(blockNumber)] =
                                Number(block.timestamp) * 1000;
                        } catch (_error) {
                            // ignore missing blocks
                        }
                    }),
                );
            }

            const init = {};
            const nextPaymentEventTimes = {};
            const absorbInit = (log) => {
                const pid = Number(log.args?.paymentId);
                const sender = String(log.args?.sender || '').toLowerCase();
                const receiver = String(log.args?.receiver || '').toLowerCase();
                if (Number.isNaN(pid)) return;
                if (sender !== wallet && receiver !== wallet) return;
                if (!init[pid]) init[pid] = log.transactionHash;
                const blockMs = blockTimeByNumber[String(log.blockNumber)];
                if (Number.isFinite(blockMs) && !nextPaymentEventTimes[pid]) {
                    nextPaymentEventTimes[pid] = blockMs;
                }
            };
            initLogs.forEach(absorbInit);
            lockedInitLogs.forEach(absorbInit);

            const claim = {};
            claimLogs.forEach((log) => {
                const pid = Number(log.args?.paymentId);
                if (!Number.isNaN(pid)) claim[pid] = log.transactionHash;
            });
            lockedClaimLogs.forEach((log) => {
                const pid = Number(log.args?.paymentId);
                const receiver = String(log.args?.receiver || '').toLowerCase();
                if (Number.isNaN(pid)) return;
                if (receiver !== wallet) return;
                claim[pid] = log.transactionHash;
            });

            const nextRequestAcceptedEventTimes = {};
            requestAcceptedLogs.forEach((log) => {
                const requestId = Number(log.args?.requestId);
                const requester = String(log.args?.requester || '').toLowerCase();
                const payer = String(
                    log.args?.payer || log.args?.receiver || '',
                ).toLowerCase();
                const status = Number(log.args?.status);
                if (Number.isNaN(requestId)) return;
                if (requester !== wallet && payer !== wallet) return;
                if (Number.isFinite(status) && status !== 0 && status !== 2) {
                    return;
                }
                const blockMs = blockTimeByNumber[String(log.blockNumber)];
                if (!Number.isFinite(blockMs)) return;
                nextRequestAcceptedEventTimes[requestId] = blockMs;
            });

            setPaymentEventTimes((prev) => ({
                ...nextPaymentEventTimes,
                ...prev,
            }));
            setRequestAcceptedEventTimes((prev) => ({
                ...nextRequestAcceptedEventTimes,
                ...prev,
            }));
            setTxHashMap((prev) => ({
                init: { ...init, ...prev.init },
                claim: { ...claim, ...prev.claim },
            }));
        } catch (error) {
            console.warn('Failed to fetch direct-payment tx hashes:', error?.message);
        }
    };

    // Authoritative hashes/timestamps from DB (covers full history after backfill).
    const getDirectPaymentTxHashesFromDb = async () => {
        if (!walletAddress) return;
        try {
            setChartLoading(true);
            const token = getStoredBackendToken(walletAddress);
            if (!token) return;

            const records = await listMyDirectPayments(token);
            if (!Array.isArray(records)) return;

            setPaymentRecords(records);

            if (records.length === 0) return;

            const init = {};
            const claim = {};
            const timesFromDb = {};
            records.forEach((record) => {
                const pid = Number(record.paymentId);
                if (!Number.isFinite(pid)) return;
                if (record.txHash) init[pid] = record.txHash;
                if (record.claimTxHash) claim[pid] = record.claimTxHash;
                if (record.createdAt) {
                    const ms = new Date(record.createdAt).getTime();
                    if (Number.isFinite(ms) && ms > 0) {
                        timesFromDb[pid] = ms;
                    }
                }
            });

            setTxHashMap((prev) => ({
                init: { ...prev.init, ...init },
                claim: { ...prev.claim, ...claim },
            }));
            if (Object.keys(timesFromDb).length) {
                setPaymentEventTimes((prev) => ({
                    ...timesFromDb,
                    ...prev,
                }));
            }
        } catch (error) {
            console.warn(
                'Failed to fetch direct payments from backend:',
                error?.response?.data?.message || error?.message,
            );
        } finally {
            setChartLoading(false);
        }
    };

    const handleChangeFilter = (name) => {
        setFilterName(name);
        setHistoryPage(1);
    };

    useEffect(() => {
        const { totalAmount, paymentCount, disputedCount } = aggregateInEscrowMetrics([
            ...sendData,
            ...receiveData,
        ]);
        setTotalInEscrowAmount(totalAmount);
        setInEscrowPaymentCount(paymentCount);
        setInEscrowDisputedCount(disputedCount);
    }, [sendData, receiveData]);

    useEffect(() => {
        if (
            !Object.keys(paymentEventTimes).length &&
            !Object.keys(requestAcceptedEventTimes).length
        ) {
            return;
        }
        setReceiveData((prev) =>
            prev.length
                ? sortDirectPaymentsNewestFirst(
                      prev,
                      paymentEventTimes,
                      requestAcceptedEventTimes,
                  )
                : prev,
        );
        setSendData((prev) =>
            prev.length
                ? sortDirectPaymentsNewestFirst(
                      prev,
                      paymentEventTimes,
                      requestAcceptedEventTimes,
                  )
                : prev,
        );
    }, [paymentEventTimes, requestAcceptedEventTimes]);

    // Chart totals: completed direct payments + accepted payment requests only.
    useEffect(() => {
        if (!walletAddress) {
            setChartDataset(buildEmptyChartBuckets(chartTimelineDays));
            return;
        }

        const directMap = new Map();
        [...sendData, ...receiveData]
            .filter((row) => String(row?.type || 'Direct').toLowerCase() === 'direct')
            .forEach((row) => {
            const id = Number(row.id ?? row.paymentId);
            if (!Number.isNaN(id)) {
                directMap.set(id, row);
            }
        });

        setChartDataset(
            buildDashboardChartDataset({
                walletAddress,
                dayCount: chartTimelineDays,
                directPayments: [...directMap.values()],
                outgoingPaymentRequests,
                incomingPaymentRequests,
                dbRecords: paymentRecords,
                paymentEventTimes,
                requestAcceptedEventTimes,
            }),
        );
    }, [
        chartTimelineDays,
        walletAddress,
        sendData,
        receiveData,
        collectPaymentData,
        outgoingPaymentRequests,
        incomingPaymentRequests,
        paymentRecords,
        paymentEventTimes,
        requestAcceptedEventTimes,
    ]);

    useEffect(() => {
        if (!(isConnected && chainId === targetChainId && isBackendReady)) {
            return undefined;
        }

        let cancelled = false;
        const loadDashboard = async () => {
            // DB-first history/chart — avoids slow on-chain mapping scans.
            await Promise.all([
                getSendDirectPaymentData(),
                getReceiveDirectPaymentData(),
                getPendingLockedClaims(),
                getPaymentRequestsForChart(),
                getDirectPaymentTxHashesFromDb(),
            ]);
            if (cancelled) return;
            // Soft fill only for recent missing hashes/timestamps.
            await getDirectPaymentTxHashes();
        };

        loadDashboard();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line
    }, [walletAddress, isBackendReady]);

    return (
        <SkeletonTheme baseColor={skeletonBase} highlightColor={skeletonHighlight}>
            <div className="crypto-page relative min-h-[calc(100vh-80px)] text-zinc-900 dark:text-zinc-100">
                <div className="relative z-10 mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
                    {!isConnected ? (
                        <div className="flex flex-col items-center justify-center min-h-[60vh] space-y-8 animate-fade-in">
                            <div className="text-center max-w-2xl mx-auto space-y-5 px-4">
                                <div className="inline-flex items-center gap-2 rounded-full border border-zinc-200 bg-white px-4 py-1.5 mb-2 shadow-sm dark:border-zinc-800 dark:bg-zinc-900/80 dark:shadow-none">
                                    <span className="h-2 w-2 rounded-full bg-teal-600 dark:bg-teal-500" />
                                    <span className="text-[11px] font-semibold uppercase tracking-wide text-zinc-600 dark:text-zinc-400">
                                        CronPay
                                    </span>
                                </div>
                                <h1 className="text-3xl md:text-5xl font-semibold tracking-tight leading-[1.15] text-zinc-900 dark:text-white">
                                    Your on-chain{' '}
                                    <span className="text-teal-700 dark:text-teal-400">payment hub</span>
                                </h1>
                                <p className="text-sm md:text-base text-zinc-600 dark:text-zinc-400 max-w-lg mx-auto leading-relaxed">
                                    Connect your wallet to send, receive, and manage payments with contract-backed settlement.
                                </p>
                            </div>
                            <ConnectWalletButton />
                            <div className="grid grid-cols-3 gap-6 mt-6 max-w-md mx-auto">
                                {[{ icon: 'fa-bolt', label: 'Instant' }, { icon: 'fa-shield-halved', label: 'Secure' }, { icon: 'fa-link', label: 'On-chain' }].map((item, i) => (
                                    <div key={i} className="flex flex-col items-center gap-1.5">
                                        <div className="flex h-10 w-10 items-center justify-center rounded-lg border border-zinc-200 bg-white shadow-sm dark:border-zinc-800 dark:bg-zinc-900 dark:shadow-none">
                                            <i className={`fa-solid ${item.icon} text-sm text-teal-700 dark:text-teal-500`} />
                                        </div>
                                        <span className="text-[11px] font-medium text-zinc-500 dark:text-zinc-500">{item.label}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : chainId !== targetChainId ? (
                        <div className="flex flex-col items-center justify-center min-h-[60vh] space-y-6 animate-fade-in">
                            <div className="flex h-20 w-20 items-center justify-center rounded-2xl border border-amber-200 bg-amber-50 dark:border-amber-900/50 dark:bg-amber-950/40">
                                <i className="fas fa-exclamation-triangle text-3xl text-amber-600 dark:text-amber-400" />
                            </div>
                            <h2 className="text-2xl font-semibold text-zinc-900 dark:text-white">Wrong network</h2>
                            <p className="text-sm text-zinc-600 dark:text-zinc-400">Switch to the configured network to continue.</p>
                            <SwitchNetworkButton />
                        </div>
                    ) : !isBackendReady ? (
                        <BackendWalletSignInPanel
                            walletAddress={walletAddress}
                            status={authStatus}
                            error={authError}
                            onRetry={signIn}
                            title="Register for payment history"
                            description="Sign a free message to link your wallet to CronPay and load saved payment records on this dashboard."
                        />
                    ) : (
                        <div className="animate-fade-in space-y-8">
                            <DashboardAlerts />

                            {/* PAGE HEADER */}
                            <div className="flex flex-col gap-5 xl:flex-row xl:items-start xl:justify-between">
                                <div>
                                    <h1 className="text-2xl font-semibold tracking-tight text-zinc-900 md:text-3xl dark:text-white">
                                        Payment overview
                                    </h1>
                                    <div className="mt-2 flex flex-wrap items-center gap-2 text-[12px] text-zinc-600 dark:text-zinc-400">
                                        <span className="inline-flex items-center gap-1.5">
                                            <span className="h-1.5 w-1.5 rounded-full bg-teal-600 dark:bg-teal-500" />
                                            Connected
                                        </span>
                                        <span className="font-mono text-zinc-800 dark:text-zinc-200">
                                            {shortenWalletAddress(walletAddress)}
                                        </span>
                                        <span className="text-zinc-300 dark:text-zinc-600">·</span>
                                        <span>{chainName || 'Network'}</span>
                                        <span className="text-zinc-300 dark:text-zinc-600">·</span>
                                        <span className="text-zinc-500 dark:text-zinc-500">Direct payments</span>
                                    </div>
                                </div>
                                <div className="flex flex-wrap items-center justify-end gap-2 sm:justify-start xl:justify-end">
                                        <Link
                                            to="/pay"
                                            className="inline-flex items-center gap-2 rounded-lg bg-teal-700 px-4 py-2.5 text-[12px] font-semibold text-white shadow-sm transition-colors hover:bg-teal-800 dark:bg-teal-600 dark:hover:bg-teal-500"
                                        >
                                            <i className="fa-solid fa-arrow-up-long" />
                                            Send payment
                                        </Link>
                                        <Link
                                            to="/get-paid"
                                            className="inline-flex items-center gap-2 rounded-lg border border-zinc-300 bg-white px-4 py-2.5 text-[12px] font-semibold text-zinc-800 transition-colors hover:bg-zinc-50 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200 dark:hover:bg-zinc-800"
                                        >
                                            <i className="fa-solid fa-arrow-down-long" />
                                            Request
                                        </Link>
                                        <Link
                                            to="/on-going"
                                            className="inline-flex items-center gap-2 rounded-lg border border-zinc-300 bg-white px-4 py-2.5 text-[12px] font-semibold text-zinc-800 transition-colors hover:bg-zinc-50 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200 dark:hover:bg-zinc-800"
                                        >
                                            <i className="fa-regular fa-clock" />
                                            Ongoing
                                        </Link>
                                </div>
                            </div>

                            {/* STATS GRID */}
                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                                {[
                                    {
                                        label: 'Total sent',
                                        value: totalSendAmount,
                                        valueColor:
                                            'text-zinc-900 dark:text-white',
                                        icon: 'fa-arrow-up-long',
                                        iconColor:
                                            'text-zinc-700 dark:text-zinc-300',
                                        iconBg:
                                            'border border-zinc-200 bg-zinc-100 dark:border-zinc-700 dark:bg-zinc-800/80',
                                        meta: `${sendData.length} ${sendData.length === 1 ? 'transaction' : 'transactions'}`,
                                        loading: sendDataLoading,
                                    },
                                    {
                                        label: 'Total received',
                                        value: totalReceiveAmount,
                                        valueColor:
                                            'text-zinc-900 dark:text-white',
                                        icon: 'fa-arrow-down-long',
                                        iconColor: 'text-teal-800 dark:text-teal-400',
                                        iconBg:
                                            'border border-teal-200 bg-teal-50 dark:border-teal-900/50 dark:bg-teal-950/40',
                                        meta: `${receiveData.length} ${receiveData.length === 1 ? 'transaction' : 'transactions'}`,
                                        loading: receiveDataLoading,
                                    },
                                    {
                                        label: 'Claimable now',
                                        value: totalClaimableAmount,
                                        valueColor:
                                            collectPaymentData.length > 0
                                                ? 'text-teal-800 dark:text-teal-300'
                                                : 'text-zinc-900 dark:text-white',
                                        icon: 'fa-link',
                                        iconColor: 'text-teal-800 dark:text-teal-400',
                                        iconBg:
                                            'border border-teal-200 bg-teal-50 dark:border-teal-900/50 dark:bg-teal-950/40',
                                        meta: `${collectPaymentData.length} ${collectPaymentData.length === 1 ? 'payment' : 'payments'}`,
                                        loading: receiveDataLoading,
                                    },
                                    {
                                        label: 'In escrow',
                                        value: totalInEscrowAmount,
                                        valueColor:
                                            totalInEscrowAmount > 0
                                                ? 'text-teal-800 dark:text-teal-300'
                                                : 'text-zinc-900 dark:text-white',
                                        icon: 'fa-vault',
                                        iconColor: 'text-teal-800 dark:text-teal-400',
                                        iconBg:
                                            'border border-teal-200 bg-teal-50 dark:border-teal-900/50 dark:bg-teal-950/40',
                                        meta:
                                            inEscrowPaymentCount === 0
                                                ? 'No locked funds'
                                                : inEscrowDisputedCount > 0
                                                    ? `${inEscrowPaymentCount} ${inEscrowPaymentCount === 1 ? 'payment' : 'payments'} · ${inEscrowDisputedCount} disputed`
                                                    : `${inEscrowPaymentCount} ${inEscrowPaymentCount === 1 ? 'payment' : 'payments'}`,
                                        loading: sendDataLoading || receiveDataLoading,
                                    },
                                ].map((stat, i) => (
                                    <div
                                        key={i}
                                        className="relative overflow-hidden rounded-xl border border-zinc-200 bg-white p-5 shadow-sm transition-shadow dark:border-zinc-800 dark:bg-zinc-900/80 dark:shadow-none"
                                    >
                                        <div className="mb-4 flex items-start justify-between">
                                            <div
                                                className={`flex h-9 w-9 items-center justify-center rounded-lg ${stat.iconBg}`}
                                            >
                                                <i
                                                    className={`fa-solid ${stat.icon} ${stat.iconColor} text-sm`}
                                                />
                                            </div>
                                            {stat.meta && (
                                                <span className="mt-1.5 text-[10px] font-medium text-zinc-500 dark:text-zinc-500">
                                                    {stat.meta}
                                                </span>
                                            )}
                                        </div>
                                        <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-zinc-500 dark:text-zinc-500">
                                            {stat.label}
                                        </p>
                                        <div
                                            className={`text-2xl font-semibold tabular-nums leading-tight md:text-[26px] ${stat.valueColor}`}
                                        >
                                            {stat.loading ? (
                                                <Loader size="18px" />
                                            ) : stat.isCount ? (
                                                stat.value
                                            ) : (
                                                <AmountDisplay
                                                    value={stat.value}
                                                    className={stat.valueColor}
                                                    tabular
                                                />
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* TRANSACTION VOLUME CHART */}
                            <TransactionVolumeChart
                                data={chartDataset}
                                loading={chartLoading}
                                subtitle={`Daily sent vs received · ${dailyPresetLabel(chartTimelineDays)}`}
                                enableTimeline
                                timelineMode="days"
                                timelineDays={chartTimelineDays}
                                onTimelineChange={(days) => setChartTimelineDays(days)}
                            />

                            {/* HISTORY TABLE */}
                            <div className="overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-sm dark:border-zinc-800 dark:bg-zinc-950/50 dark:shadow-none md:rounded-2xl">
                                <div className="flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
                                    <div>
                                        <h2 className="text-[17px] font-semibold text-zinc-900 dark:text-white">
                                            Transaction history
                                        </h2>
                                        <p className="mt-0.5 text-[11px] text-zinc-500 dark:text-zinc-500">
                                            Direct, milestone, and payment requests {filterName === SEND ? 'you have sent' : 'you have received'}
                                        </p>
                                    </div>
                                    <div className="inline-flex items-center gap-0.5 self-start rounded-lg border border-teal-200/80 bg-teal-50/70 p-0.5 sm:self-auto dark:border-teal-800/50 dark:bg-teal-950/40">
                                        {homeFilterData.map((item) => {
                                            const active = filterName === item.name;
                                            const label = item.name === SEND ? 'Sent' : 'Receiving';
                                            return (
                                                <button
                                                    key={item.id}
                                                    type="button"
                                                    onClick={() => handleChangeFilter(item.name)}
                                                    className={`flex items-center gap-1.5 whitespace-nowrap rounded-md px-3.5 py-1.5 text-[12px] font-semibold transition-colors ${
                                                        active
                                                            ? 'bg-teal-600 text-white shadow-sm shadow-teal-900/25 dark:bg-teal-600 dark:text-white dark:shadow-teal-950/50'
                                                            : 'text-teal-900/70 hover:bg-teal-100/80 hover:text-teal-900 dark:text-teal-200/65 dark:hover:bg-teal-900/50 dark:hover:text-teal-100'
                                                    }`}
                                                >
                                                    <i className={`${item.icon} text-[10px]`} />
                                                    {label}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                                <TransactionHistoryList
                                    rows={
                                        filterName === RECEIVE ? receiveData : sendData
                                    }
                                    loading={
                                        filterName === RECEIVE
                                            ? receiveDataLoading
                                            : sendDataLoading
                                    }
                                    mode={filterName === RECEIVE ? 'receive' : 'send'}
                                    counterpartyLabel={filterName === SEND ? 'Receiver' : 'Sender'}
                                    counterpartyKey={filterName === SEND ? 'receiver' : 'sender'}
                                    txHashMap={txHashMap}
                                    explorerUrl={blockExplorerUrl}
                                    pageSize={HISTORY_PAGE_SIZE}
                                    page={historyPage}
                                    onPageChange={setHistoryPage}
                                    emptyTitle={filterName === SEND ? 'No outgoing payments' : 'No received payments'}
                                    emptyDescription={
                                        filterName === SEND
                                            ? 'Direct payments, milestones, and requests you send will show up here.'
                                            : 'Direct payments, milestones, and requests you receive will show up here.'
                                    }
                                    emptyIcon={filterName === SEND ? 'fa-paper-plane' : 'fa-inbox'}
                                />
                            </div>

                            {/* COLLECT PAYMENT TABLE */}
                            <div className="overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-sm dark:border-zinc-800 dark:bg-zinc-950/50 dark:shadow-none md:rounded-2xl">
                                <div className="flex items-center justify-between gap-3 px-5 py-4 sm:px-6">
                                    <div>
                                        <h2 className="text-[17px] font-semibold text-zinc-900 dark:text-white">
                                            Pending claims
                                        </h2>
                                        <p className="mt-0.5 text-[11px] text-zinc-500 dark:text-zinc-500">
                                            Milestone escrow ready to claim now
                                        </p>
                                    </div>
                                    {collectPaymentData.length > 0 && (
                                        <span className="inline-flex items-center gap-1.5 rounded-full border border-teal-200 bg-teal-50 px-3 py-1 text-[11px] font-semibold text-teal-800 dark:border-teal-900/60 dark:bg-teal-950/50 dark:text-teal-300">
                                            <span className="h-1.5 w-1.5 rounded-full bg-teal-600 dark:bg-teal-500" />
                                            {collectPaymentData.length} pending
                                        </span>
                                    )}
                                </div>
                                <TransactionHistoryList
                                    rows={collectPaymentData}
                                    loading={receiveDataLoading}
                                    mode="claim"
                                    defaultType="Milestone"
                                    counterpartyLabel="From Sender"
                                    counterpartyKey="sender"
                                    txHashMap={txHashMap}
                                    explorerUrl={blockExplorerUrl}
                                    pageSize={HISTORY_PAGE_SIZE}
                                    actionLabel="Claim"
                                    actionTone="emerald"
                                    onAction={(row) => {
                                        setPaymentId(Number(row.paymentId));
                                        setClaimModal(true);
                                    }}
                                    emptyTitle="Nothing to claim"
                                    emptyDescription="Milestone payments with funds available to claim will appear here."
                                    emptyIcon="fa-hand-holding-dollar"
                                />
                            </div>
                        </div>
                    )}
                </div>

                {/* MODALS */}
                {disputeModal && <DisputeModal setDisputeModal={setDisputeModal} currentItem={currentItem} setCurrentItem={setCurrentItem} />}
                {claimModal && (
                    <ClaimPayment
                        setClaimModal={setClaimModal}
                        paymentId={paymentId}
                        setPaymentId={setPaymentId}
                        onWriteError={onWriteError}
                        onWriteSuccess={onWriteSuccess}
                        getData={getPendingLockedClaims}
                        getReceiveLockedPaymentData={getPendingLockedClaims}
                        paymentType="LockedPayment"
                    />
                )}
            </div>
        </SkeletonTheme>
    );
}

export default Home